import { Lightbulb, AlertTriangle, Wrench } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface DiagnosisContentProps {
  recommendedParts: string[];
  onProceedToRepair?: () => void;
  onConsiderRefurbishment?: () => void;
}

export function DiagnosisContent({
  recommendedParts,
  onProceedToRepair,
  onConsiderRefurbishment,
}: DiagnosisContentProps) {
  const { t } = useTranslation('repair');

  const handleProceedToRepair = () => {
    toast.success(t('diagnosis.starting'));
    onProceedToRepair?.();
  };

  const handleConsiderRefurbishment = () => {
    toast.info(t('diagnosis.openingRefurb'));
    onConsiderRefurbishment?.();
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Lightbulb className="w-5 h-5 text-repair" />
        <h4 className="font-semibold text-foreground">{t('diagnosis.title')}</h4>
      </div>

      <div className="bg-background rounded-xl border border-border p-5 mb-6">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-muted-foreground mb-1">{t('diagnosis.recommendedAction')}</p>
            <p className="text-foreground">{t('diagnosis.summary')}</p>
          </div>
        </div>

        <p className="text-foreground ml-8">
          <span className="font-semibold">{t('diagnosis.recommendedParts')}</span>{" "}
          {recommendedParts.join(", ")}
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Button
          onClick={handleProceedToRepair}
          className="bg-repair hover:bg-repair/90 text-repair-foreground"
        >
          <Wrench className="w-4 h-4 mr-2" />
          {t('diagnosis.proceed')}
        </Button>
        <Button variant="outline" onClick={handleConsiderRefurbishment}>
          {t('diagnosis.consider')}
        </Button>
      </div>
    </div>
  );
}
